import { GroupingModeEnum } from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { SINGLETON_GROUP_LABEL } from '../../../components/ServiceUsages/GroupBanner';
import { filterRetainedServiceUsagesGroups } from '../../../utils/retained-service-usages-groups.util';

import type { AggregationLevelGroupInput } from './aggregation-level-group.type';

// A null value is one the user may not read: its VAT is unknown.
type ServiceValueVat = Readonly<{ value: Readonly<{ vatId: Id }> | null }>;

type BaseServiceUsagesGroup = Readonly<{
  _id: Id;
  groupingMode: GroupingModeEnum;
  isOptional?: boolean;
  label?: string | null;
  totalMode: AggregationLevelGroupInput['totalMode'];
}>;

export type AccountingServiceUsagesGroup = BaseServiceUsagesGroup &
  Readonly<{
    manualTotal?: Readonly<{ vatId: Readonly<{ value?: Id | null }> }> | null;
    serviceUsages: readonly Readonly<{
      compiled: Readonly<{
        consolidation: Readonly<{ service: ServiceValueVat }>;
      }>;
    }>[];
  }>;

export type AccountingSnapshotServiceUsagesGroup = BaseServiceUsagesGroup &
  Readonly<{
    manualTotal?: Readonly<{ vatId: Id }> | null;
    serviceUsages: readonly Readonly<{
      consolidation: Readonly<{ service: ServiceValueVat }>;
    }>[];
  }>;

const buildGroupLabel = (group: BaseServiceUsagesGroup): string =>
  group.groupingMode === GroupingModeEnum.Singleton
    ? SINGLETON_GROUP_LABEL
    : (group.label ?? '');

export function buildAggregationLevelGroups(
  groups: readonly AccountingServiceUsagesGroup[],
): readonly AggregationLevelGroupInput[] {
  return groups.map((group) => ({
    id: group._id,
    label: buildGroupLabel(group),
    totalMode: group.totalMode,
    manualTotalVatId: group.manualTotal?.vatId.value,
    serviceUsageVatIds: group.serviceUsages
      .map((usage) => usage.compiled.consolidation.service.value?.vatId)
      .filter(isDefined),
  }));
}

export function buildSnapshotAggregationLevelGroups(
  groups: readonly AccountingSnapshotServiceUsagesGroup[],
): readonly AggregationLevelGroupInput[] {
  return groups.map((group) => ({
    id: group._id,
    label: buildGroupLabel(group),
    totalMode: group.totalMode,
    manualTotalVatId: group.manualTotal?.vatId,
    serviceUsageVatIds: group.serviceUsages
      .map((usage) => usage.consolidation.service.value?.vatId)
      .filter(isDefined),
  }));
}

// The Pennylane quote is built from the frozen quotation snapshot groups
// retained by the client choice, the same data the back reads to check the
// ServiceUsage level's availability and to resolve the default level
// (check-accounting-info-state-before-update.pipe.ts).
export function buildEffectiveAggregationLevelGroups({
  liveGroups,
  selectedOptionGroupIds,
  snapshotGroups,
}: Readonly<{
  liveGroups: readonly AccountingServiceUsagesGroup[];
  selectedOptionGroupIds?: readonly Id[] | null;
  snapshotGroups?: readonly AccountingSnapshotServiceUsagesGroup[] | null;
}>): readonly AggregationLevelGroupInput[] {
  return snapshotGroups
    ? buildSnapshotAggregationLevelGroups(
        filterRetainedServiceUsagesGroups(
          snapshotGroups,
          selectedOptionGroupIds,
        ),
      )
    : buildAggregationLevelGroups(
        filterRetainedServiceUsagesGroups(liveGroups, selectedOptionGroupIds),
      );
}
